import { Space, Tag } from 'antd';
import React, { type ReactNode } from 'react';
import type { CrmProColumnCompat } from '@/modules/zy-crm/types/pro-table-compat';
import {
  formatCrmDate,
  resolveRelationText,
} from '@/modules/zy-crm/utils/display';

export type InvoiceTableRecord = {
  uuid: string;
  code?: string;
  customerUuid?: string;
  customerName?: string;
  contractUuid?: string;
  contractName?: string;
  ownerUserUuid?: string;
  ownerUserName?: string;
  invoiceDate?: string;
  invoiceType?: number;
  auditStatus?: number;
  totalAmount?: string;
  titleSnapshot?: string;
  remark?: string;
  createdAt?: string;
  processInstanceUuid?: string;
  workflowInstanceUuid?: string;
  workflowStatus?: string;
  workflowTitle?: string;
  workflowStartedAt?: string;
  workflowFinishedAt?: string;
  workflowRejectedRemark?: string;
};

type Translate = (
  id: string,
  defaultMessage: string,
  values?: Record<string, any>,
) => string;

type RenderRecord<T> = (record: T) => ReactNode;

type BuildInvoiceTableColumnsOptions<T extends InvoiceTableRecord> = {
  t: Translate;
  memberNameMap?: Record<string, string>;
  canOpenDetail?: (record: T) => boolean;
  onOpenDetail?: (record: T) => void;
  renderCustomer?: RenderRecord<T>;
  renderContract?: RenderRecord<T>;
  renderOwner?: RenderRecord<T>;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
};

export const INVOICE_TYPE_OPTIONS = [
  {
    value: 1,
    key: 'pages.zy-crm.invoice.option.type.normal',
    fallback: '普通发票',
  },
  {
    value: 2,
    key: 'pages.zy-crm.invoice.option.type.special',
    fallback: '专用发票',
  },
];

export const AUDIT_STATUS_OPTIONS = [
  {
    value: 0,
    key: 'pages.zy-crm.invoice.option.status.draft',
    fallback: '草稿',
    color: 'default',
  },
  {
    value: 1,
    key: 'pages.zy-crm.invoice.option.status.pending',
    fallback: '待审核',
    color: 'processing',
  },
  {
    value: 2,
    key: 'pages.zy-crm.invoice.option.status.approved',
    fallback: '已通过',
    color: 'success',
  },
  {
    value: 3,
    key: 'pages.zy-crm.invoice.option.status.rejected',
    fallback: '已驳回',
    color: 'error',
  },
];

export const canEditInvoice = (record: InvoiceTableRecord) =>
  Number(record?.auditStatus ?? 0) !== 2;

const renderInvoiceStatusTag = (t: Translate, value?: unknown): ReactNode => {
  const option = AUDIT_STATUS_OPTIONS.find(
    (item) => item.value === Number(value),
  );
  return (
    <Tag color={option?.color}>
      {t(option?.key || '', option?.fallback || '-')}
    </Tag>
  );
};

const renderInvoiceTypeText = (t: Translate, value?: unknown): ReactNode => {
  const option = INVOICE_TYPE_OPTIONS.find(
    (item) => item.value === Number(value),
  );
  return t(option?.key || '', option?.fallback || '-') || String(value ?? '-');
};

export function buildInvoiceTableColumns<T extends InvoiceTableRecord>({
  t,
  memberNameMap,
  canOpenDetail,
  onOpenDetail,
  renderCustomer,
  renderContract,
  renderOwner,
  onEdit,
  onDelete,
}: BuildInvoiceTableColumnsOptions<T>): CrmProColumnCompat<T>[] {
  return [
    {
      title: t('pages.zy-crm.invoice.field.code', '发票申请编号'),
      dataIndex: 'code',
      key: 'code',
      search: false,
      fixed: 'left',
      width: 180,
      render: (value, record) => {
        const displayValue = value || '-';
        if (!onOpenDetail) {
          return displayValue;
        }
        if (canOpenDetail && !canOpenDetail(record)) {
          return displayValue;
        }
        return <a onClick={() => onOpenDetail(record)}>{displayValue}</a>;
      },
    },
    {
      title: t('pages.zy-crm.invoice.field.customerUuid', '客户'),
      dataIndex: 'customerUuid',
      key: 'customerUuid',
      search: false,
      width: 180,
      render: (_, record) =>
        renderCustomer?.(record) ??
        resolveRelationText({
          value: record.customerUuid,
          directName: record.customerName,
        }),
    },
    {
      title: t('pages.zy-crm.invoice.field.contractUuid', '合同'),
      dataIndex: 'contractUuid',
      key: 'contractUuid',
      search: false,
      width: 180,
      render: (_, record) =>
        renderContract?.(record) ??
        resolveRelationText({
          value: record.contractUuid,
          directName: record.contractName,
        }),
    },
    {
      title: t('pages.zy-crm.invoice.field.ownerUserUuid', '负责人'),
      dataIndex: 'ownerUserUuid',
      key: 'ownerUserUuid',
      search: false,
      width: 140,
      render: (_, record) =>
        renderOwner?.(record) ??
        resolveRelationText({
          value: record.ownerUserUuid,
          directName: record.ownerUserName,
          nameMap: memberNameMap,
          emptyText: t('pages.zy-crm.invoice.member.unknown', '未知成员'),
        }),
    },
    {
      title: t('pages.zy-crm.invoice.field.totalAmount', '开票金额'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      search: false,
      width: 120,
    },
    {
      title: t('pages.zy-crm.invoice.field.invoiceDate', '开票日期'),
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      search: false,
      width: 140,
      render: (value) => formatCrmDate(value, 'date'),
    },
    {
      title: t('pages.zy-crm.invoice.field.invoiceType', '开票类型'),
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      search: false,
      width: 120,
      render: (value) => renderInvoiceTypeText(t, value),
    },
    {
      title: t('pages.zy-crm.invoice.field.auditStatus', '审核状态'),
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      search: false,
      width: 120,
      render: (value) => renderInvoiceStatusTag(t, value),
    },
    {
      title: t('pages.zy-crm.invoice.field.createdAt', '创建时间'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      hideInSearch: true,
      width: 170,
      render: (value) => formatCrmDate(value),
    },
    {
      title: t('pages.zy-crm.invoice.column.operation', '操作'),
      key: 'option',
      valueType: 'option',
      fixed: 'right',
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
        },
      }),
      render: (_, record) => (
        <Space size={12} wrap={false}>
          {onEdit && canEditInvoice(record) ? (
            <a key="edit" onClick={() => onEdit(record)}>
              {t('pages.zy-crm.invoice.action.edit', '编辑')}
            </a>
          ) : null}
          {onDelete && Number(record.auditStatus ?? 0) === 0 ? (
            <a key="delete" onClick={() => onDelete(record)}>
              {t('pages.zy-crm.invoice.action.delete', '删除')}
            </a>
          ) : null}
        </Space>
      ),
    },
  ];
}
